import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getApplicationData as selectApplicationData } from 'selectors/getApplicationData';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationData, sendSmsCode, sendSmsCodeForApplicationId } from 'thunks';

import { useQueryParams } from 'hooks/useQueryParam';

import { UrlParams } from 'enums/UrlParams';

import { ReactComponent as LoadingSpinner } from 'images/loader.svg';
import { ReactComponent as GreenCheck } from 'images/green-check.svg';

import StateContainer from 'components/StateContainer';
import Button from 'components/Button';
import FormContainer from 'components/LoanForm/FormContainer';
import UserSessionWarning from 'components/Common/UserSessionWarning/UserSessionWarning';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';
import { FLOW_TO_STEP_MAP } from 'handlers/applicationStep';
import { useUnsubscribedPhoneNumber, UNSUBSCRIBED_PHONE_NUMBER_MESSAGE } from 'hooks/useUnsubscribedPhoneNumber';
import { failureNotification } from 'notifications';
import ErrorMessages from 'enums/ErrorMessages';

import VerifySms from './VerifySms';

import { EnterPhoneNumber } from './EnterPhoneNumber';

import styles from './SmsLogin.module.scss';

enum SmsAuthState {
  WaitForUserClick,
  Start,
  Sending,
  Sent,
  Success,
}

const SmsLogin = () => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const params = useQueryParams();
  const [authState, setAuthState] = useState(SmsAuthState.WaitForUserClick);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const { application } = useSelector(selectApplicationData);
  const {
    isUnsubscribedPhoneNumber,
    isSendCodeBlocked,
    handleSendSmsCodeError,
    resetUnsubscribedWarning,
  } = useUnsubscribedPhoneNumber();

  function getRedirectPath(): string | null {
    return params.get(UrlParams.ComingFrom);
  }

  const baseUrl = `${window.location.protocol}//${window.location.host}`;

  function getRedirectUrl(): string | null {
    const path = getRedirectPath();
    if (path === null) return null;
    return `${baseUrl}${getRedirectPath()}`;
  }

  function getFinancialDashboardUrl(): string | null {
    return `${baseUrl}${RoutePath.FinancialDashboard}`;
  }

  function getApplicationId(): string | null {
    const redirectUrl = getRedirectUrl();
    if (redirectUrl !== null) {
      const comingFrom = new URL(redirectUrl);
      if (comingFrom.searchParams.has(UrlParams.ResumeProcess)) {
        return comingFrom.searchParams.get(UrlParams.ResumeProcess);
      }
    }
    return null;
  }

  function comingFromHasEnterPhoneNumber(): boolean {
    const redirectUrl = getRedirectUrl();
    if (redirectUrl === null) {
      return false;
    }
    return new URL(redirectUrl).searchParams.has(UrlParams.EnterPhoneNumber);
  }

  const applicationId = getApplicationId();

  const sendSmsByApplicationId = async (appId: string) => {
    await dispatchWithUnwrap(sendSmsCodeForApplicationId({ applicationId: appId }));
  };

  const sendSmsByPhoneNumber = async () => {
    if (phoneNumber === null) {
      throw new Error('Phone number is null');
    }
    console.log('sending sms code for phone number', phoneNumber);
    await dispatchWithUnwrap(sendSmsCode({ phoneNumber }));
  };

  const onSuccessfulVerification = async () => {
    // Resume-process flow - we will fetch fresh data in routes/index.tsx
    // So we can skip the fetch here and just redirect
    if (applicationId !== null) {
      window.location.replace(getRedirectUrl()!);
      return;
    }

    // Not a resume-process flow - need to fetch fresh data and check status
    if (application?.id) {
      const { id } = application;
      const {
        application: { status: freshStatus },
      } = await dispatchWithUnwrap(getApplicationData(id));
      if (freshStatus === ApplicationStatusName.Rejected) {
        window.location.replace(getFinancialDashboardUrl() ?? '');
      } else {
        window.location.replace(baseUrl + FLOW_TO_STEP_MAP[application.currentFlow](application).path);
      }
      return;
    }
    if (getRedirectUrl() !== null) {
      window.location.replace(getRedirectUrl() ?? '');
    } else {
      setAuthState(SmsAuthState.Success);
    }
  };

  const onResendCode = () => {
    setAuthState(SmsAuthState.Start);
  };

  const onPhoneNumberEntered = (phoneNumberEntered: string) => {
    resetUnsubscribedWarning();
    setPhoneNumber(phoneNumberEntered);
    setAuthState(SmsAuthState.Start);
  };

  const onSendCodeError = (error: unknown) => {
    setAuthState(SmsAuthState.WaitForUserClick);
    if (!handleSendSmsCodeError(error)) {
      failureNotification(ErrorMessages.Default);
    }
  };

  useEffect(() => {
    if (authState === SmsAuthState.Start) {
      setAuthState(SmsAuthState.Sending);
      if (phoneNumber !== null) {
        sendSmsByPhoneNumber()
          .then(() => setAuthState(SmsAuthState.Sent))
          .catch(onSendCodeError);
      } else if (applicationId !== null) {
        sendSmsByApplicationId(applicationId)
          .then(() => setAuthState(SmsAuthState.Sent))
          .catch(onSendCodeError);
      } else {
        throw Error('Missing phoneNumber or applicationId');
      }
    }
  }, [authState]);

  const shouldAskForPhoneNumber =
    getRedirectUrl() === null ||
    applicationId === null ||
    params.has(UrlParams.EnterPhoneNumber) ||
    comingFromHasEnterPhoneNumber() ||
    window.location.hostname === 'localhost';

  if (shouldAskForPhoneNumber) {
    if (authState === SmsAuthState.WaitForUserClick) {
      return (
        <EnterPhoneNumber
          onPhoneNumberEntered={onPhoneNumberEntered}
          isSendBlocked={isSendCodeBlocked}
          warning={
            isUnsubscribedPhoneNumber ? <UserSessionWarning message={UNSUBSCRIBED_PHONE_NUMBER_MESSAGE} /> : undefined
          }
        />
      );
    }

    if (authState === SmsAuthState.Success) {
      return (
        <div className={styles.loaderContainer}>
          <StateContainer icon={<GreenCheck />} title="Success!" />
        </div>
      );
    }
  }

  if (authState === SmsAuthState.Start || authState === SmsAuthState.Sending) {
    return (
      <div className={styles.loaderContainer}>
        <StateContainer icon={<LoadingSpinner />} title="Sending verification code to your phone..." />
      </div>
    );
  }

  if (authState === SmsAuthState.WaitForUserClick) {
    return (
      <FormContainer
        title="Verify your identity"
        subtitle="To continue, let's verify it's really you. We will send you a code."
      >
        <Button className={styles.button} disabled={isSendCodeBlocked} onClick={() => setAuthState(SmsAuthState.Start)}>
          Send Code To My Phone
        </Button>
        {isUnsubscribedPhoneNumber && <UserSessionWarning message={UNSUBSCRIBED_PHONE_NUMBER_MESSAGE} />}
      </FormContainer>
    );
  }

  return (
    <div className={styles.container}>
      <VerifySms onSuccessfulVerification={onSuccessfulVerification} onResendCode={onResendCode} />
    </div>
  );
};

export default SmsLogin;
